import React from 'react';
import { PageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Factory, TrendingUp, CheckCircle, Users, Cog, Lightbulb, Award } from 'lucide-react';

const ImplementationCosts = () => {
  const costData = [
    {
      category: "QRM Alpha Training & Education",
      phases: {
        introduction: { level: "Medium", description: "QRM overview, Alpha training basics, MCT concepts" },
        analysis: { level: "Medium", description: "VSM training, Little's Law, Gemba walk preparation" },
        preparation: { level: "High", description: "Intensive QRM team training, value stream analysis" },
        pilot: { level: "High", description: "Hands-on MCT reduction training, POLCA system setup" },
        rollout: { level: "High", description: "Organization-wide QRM methodology training" }
      }
    },
    {
      category: "Value Stream Mapping (VSM)",
      phases: {
        introduction: { level: "N/A", description: "Not applicable yet" },
        analysis: { level: "High", description: "Complete current state VSM, product family analysis" },
        preparation: { level: "Medium", description: "Future state VSM design, target MCT setting" },
        pilot: { level: "Low", description: "VSM validation in pilot cell" },
        rollout: { level: "Medium", description: "VSM updates for all product families" }
      }
    },
    {
      category: "Gemba Walks & Data Collection",
      phases: {
        introduction: { level: "Low", description: "Initial observation walks" },
        analysis: { level: "High", description: "Detailed Gemba walks, MCT measurement, WIP counting" },
        preparation: { level: "Medium", description: "Systematic data collection setup" },
        pilot: { level: "Medium", description: "Continuous monitoring and measurement" },
        rollout: { level: "Medium", description: "Company-wide measurement systems" }
      }
    },
    {
      category: "Personnel Time Investment",
      phases: {
        introduction: { level: "Low", description: "Core team only involved" },
        analysis: { level: "High", description: "Cross-functional VSM teams, extensive Gemba time" },
        preparation: { level: "Medium", description: "Project planning and MCT target setting" },
        pilot: { level: "High", description: "Extra capacity, learning inefficiency during MCT reduction" },
        rollout: { level: "High", description: "Many employees involved in transition" }
      }
    },
    {
      category: "POLCA & QRM Tools Implementation",
      phases: {
        introduction: { level: "N/A", description: "Not applicable" },
        analysis: { level: "Low", description: "POLCA system design planning" },
        preparation: { level: "Medium", description: "POLCA card design, material flow planning" },
        pilot: { level: "High", description: "POLCA implementation, visual management setup" },
        rollout: { level: "High", description: "Company-wide POLCA system deployment" }
      }
    },
    {
      category: "MCT Measurement & Control Systems",
      phases: {
        introduction: { level: "N/A", description: "Not applicable" },
        analysis: { level: "Medium", description: "MCT measurement system design" },
        preparation: { level: "Medium", description: "Control system setup, KPI definition" },
        pilot: { level: "High", description: "Real-time MCT tracking implementation" },
        rollout: { level: "High", description: "Enterprise MCT monitoring systems" }
      }
    },
    {
      category: "Organizational Restructuring",
      phases: {
        introduction: { level: "N/A", description: "No changes yet" },
        analysis: { level: "Low", description: "Creating change plans based on VSM findings" },
        preparation: { level: "Medium", description: "Defining QRM cells and team structures" },
        pilot: { level: "Medium", description: "Pilot cell structure, cultural change support" },
        rollout: { level: "High", description: "Complete QRM cell-based restructuring" }
      }
    },
    {
      category: "Communication & Change Management",
      phases: {
        introduction: { level: "Low", description: "Convincing management with QRM benefits" },
        analysis: { level: "Medium", description: "VSM findings communication, MCT baseline sharing" },
        preparation: { level: "Medium", description: "QRM kick-off, MCT targets communication" },
        pilot: { level: "Medium", description: "Team support, sharing MCT successes & lessons" },
        rollout: { level: "Medium", description: "Sustaining QRM culture organization-wide" }
      }
    },
    {
      category: "Tools & Non-IT Resources",
      phases: {
        introduction: { level: "N/A", description: "Not applicable" },
        analysis: { level: "Low", description: "Basic assessment tools" },
        preparation: { level: "Medium", description: "Preparing visual boards for pilot" },
        pilot: { level: "Low", description: "POLCA cards, boards in pilot area" },
        rollout: { level: "Medium", description: "Standardizing and rolling out tools" }
      }
    },
    {
      category: "Software & IT Systems",
      phases: {
        introduction: { level: "N/A", description: "Not applicable" },
        analysis: { level: "Low", description: "Assessment software" },
        preparation: { level: "Medium", description: "Software selection and planning" },
        pilot: { level: "High", description: "Implementation in pilot cell" },
        rollout: { level: "High", description: "Licenses and integration company-wide" }
      }
    },
    {
      category: "Infrastructure & Layout",
      phases: {
        introduction: { level: "N/A", description: "Not applicable" },
        analysis: { level: "N/A", description: "Not applicable" },
        preparation: { level: "Low", description: "Small pilot layout changes" },
        pilot: { level: "Low-Medium", description: "Minor physical adjustments" },
        rollout: { level: "High", description: "Factory restructuring, possibly new equipment" }
      }
    },
    {
      category: "External Expertise",
      phases: {
        introduction: { level: "Low", description: "Consultant introduction session" },
        analysis: { level: "Medium", description: "Quick scan advisory costs" },
        preparation: { level: "Medium", description: "Change coach, pilot planning guidance" },
        pilot: { level: "Medium", description: "Consultant pilot guidance, benchmarks" },
        rollout: { level: "Medium", description: "Continued coaching until embedding" }
      }
    }
  ];

  const getLevelColor = (level) => {
    switch(level.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'low-medium': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-500 border-gray-200';
    }
  };

  const phases = [
    { key: 'introduction', title: 'Introduction', description: 'Initial awareness and buy-in' },
    { key: 'analysis', title: 'Analysis', description: 'Current state assessment' },
    { key: 'preparation', title: 'Preparation', description: 'Planning and setup' },
    { key: 'pilot', title: 'Pilot', description: 'Testing in controlled environment' },
    { key: 'rollout', title: 'Full Rollout', description: 'Organization-wide implementation' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <PageSEO path="/implementation-costs" />
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">QRM Implementation Costs & Investment Analysis</h1>
            <p className="text-xl text-gray-600 mb-2">
              Understanding the financial investment required for successful QRM transformation
            </p>
            <p className="text-lg text-gray-500">
              Comprehensive breakdown of cost categories across all implementation phases
            </p>
          </div>

          {/* Key Insights */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Lightbulb className="mr-3 h-6 w-6 text-blue-600" />
              Key Cost Insights
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-green-600 mb-2">Low</div>
                <div className="text-sm text-gray-600">Introduction Phase Costs</div>
                <p className="text-xs text-gray-500 mt-2">Relatively inexpensive to get started</p>
              </div>
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-orange-600 mb-2">Medium-High</div>
                <div className="text-sm text-gray-600">Pilot Phase Investment</div>
                <p className="text-xs text-gray-500 mt-2">Moderate investment for testing</p>
              </div>
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-red-600 mb-2">Highest</div>
                <div className="text-sm text-gray-600">Full Rollout Costs</div>
                <p className="text-xs text-gray-500 mt-2">Largest investment for organization-wide change</p>
              </div>
            </div>
          </Card>

          {/* Implementation Phases Timeline */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Implementation Phases</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {phases.map((phase, index) => (
                <div key={phase.key} className="text-center p-4 bg-gradient-to-b from-gray-50 to-gray-100 rounded-lg">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                    {index + 1}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{phase.title}</h3>
                  <p className="text-xs text-gray-600">{phase.description}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Cost Breakdown Table */}
          <Card className="p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Cost Categories by Implementation Phase</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-4 font-bold text-gray-900 border border-gray-200">Cost Category</th>
                    {phases.map(phase => (
                      <th key={phase.key} className="text-center p-3 font-bold text-gray-900 border border-gray-200 min-w-32">
                        {phase.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {costData.map((category, index) => (
                    <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-25"}>
                      <td className="p-4 font-medium text-gray-900 border border-gray-200">
                        {category.category}
                      </td>
                      {phases.map(phase => {
                        const phaseData = category.phases[phase.key];
                        return (
                          <td key={phase.key} className="p-3 text-center border border-gray-200">
                            <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getLevelColor(phaseData.level)}`}>
                              {phaseData.level}
                            </div>
                            <p className="text-xs text-gray-600 mt-2 leading-tight">
                              {phaseData.description}
                            </p>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Cost Legend */}
          <Card className="p-6 mb-12 bg-gray-50">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Cost Level Legend</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 bg-green-100 text-green-800 border border-green-200 rounded-full text-sm font-medium">Low</span>
                <span className="text-sm text-gray-600">Minimal investment required</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 border border-yellow-200 rounded-full text-sm font-medium">Medium</span>
                <span className="text-sm text-gray-600">Moderate investment needed</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 bg-red-100 text-red-800 border border-red-200 rounded-full text-sm font-medium">High</span>
                <span className="text-sm text-gray-600">Significant investment required</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="px-3 py-1 bg-gray-100 text-gray-500 border border-gray-200 rounded-full text-sm font-medium">N/A</span>
                <span className="text-sm text-gray-600">Not applicable in this phase</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              <strong>Note:</strong> Cost levels are relative within the context of a single QRM project and indicate which phases carry the heaviest financial burden.
            </p>
          </Card>

          {/* Cost Progression Insights */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <TrendingUp className="mr-3 h-6 w-6 text-yellow-600" />
              QRM Cost Progression Reality (Real Experience)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Initial Costs vs Long-term Benefits</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-yellow-500 mt-0.5" />
                    <span><strong>Phase 1-2:</strong> Relatively inexpensive knowledge gathering</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-orange-500 mt-0.5" />
                    <span><strong>Phase 3-4:</strong> Medium investments in pilot preparation and testing</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-red-500 mt-0.5" />
                    <span><strong>Phase 5:</strong> Highest investment when organization-wide changes occur</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="mr-2 h-5 w-5 text-green-500 mt-0.5" />
                    <span><strong>Result:</strong> ~80% lead time reduction, ~25% cost reduction achieved</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Investing in People & Culture</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Users className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Human factor is key:</strong> Large part of costs in training, coaching, change management</span>
                  </li>
                  <li className="flex items-start">
                    <Users className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Multidisciplinary teams:</strong> Cross-training requires time and investment</span>
                  </li>
                  <li className="flex items-start">
                    <Users className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>Cultural transformation:</strong> Open communication from management to shop floor</span>
                  </li>
                  <li className="flex items-start">
                    <Users className="mr-2 h-5 w-5 text-blue-500 mt-0.5" />
                    <span><strong>QRM culture creation:</strong> Essential for sustained success</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Technology as Enabler</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <Cog className="mr-2 h-5 w-5 text-purple-500 mt-0.5" />
                    <span><strong>Targeted IT investments:</strong> Visual management, real-time planning software</span>
                  </li>
                  <li className="flex items-start">
                    <Cog className="mr-2 h-5 w-5 text-purple-500 mt-0.5" />
                    <span><strong>Proven Examples:</strong> PROPOS (planning), Azumuta (digital instructions)</span>
                  </li>
                  <li className="flex items-start">
                    <Cog className="mr-2 h-5 w-5 text-purple-500 mt-0.5" />
                    <span><strong>High pilot/rollout costs:</strong> But increase transparency and flexibility</span>
                  </li>
                  <li className="flex items-start">
                    <Cog className="mr-2 h-5 w-5 text-purple-500 mt-0.5" />
                    <span><strong>ROI through efficiency:</strong> Technology investments pay back through gains</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-white rounded-lg border border-yellow-300">
              <h4 className="font-bold text-yellow-800 mb-3">💡 Jan Pauwels Implementation Wisdom:</h4>
              <p className="text-yellow-700 italic leading-relaxed">
                "Companies must abandon short-term cost thinking and look at the end result after three years. 
                This helped us persevere and keep investing in digitalization. The phased approach allows companies 
                to spread investments and maintain control while achieving dramatic improvements."
              </p>
            </div>
          </Card>

          {/* Success Reference */}
          <Card className="p-8 mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Award className="mr-3 h-6 w-6 text-blue-600" />
              Real-World Validation: Belgian Manufacturer Case Study
            </h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              <strong>A Belgian sheet metal manufacturer</strong> demonstrates that while QRM implementation requires significant upfront investment 
              across multiple cost categories, the medium-term returns justify the expense through dramatic improvements in lead time, 
              quality, and cost efficiency.
            </p>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-green-600 mb-2">Significant</div>
                <div className="text-sm text-gray-600">Revenue Increase</div>
                <div className="text-xs text-gray-500 mt-1">Over multiple years</div>
              </div>
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-blue-600 mb-2">Dramatic</div>
                <div className="text-sm text-gray-600">Lead Time Reduction</div>
                <div className="text-xs text-gray-500 mt-1">Throughput improvement</div>
              </div>
              <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                <div className="text-3xl font-bold text-purple-600 mb-2">Strong</div>
                <div className="text-sm text-gray-600">EBITDA Margin</div>
                <div className="text-xs text-gray-500 mt-1">Long-term achievement</div>
              </div>
            </div>
          </Card>

          {/* Executive Summary */}
          <Card className="p-8 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Executive Summary</h2>
            <div className="text-gray-700 space-y-4">
              <p className="leading-relaxed">
                <strong>QRM implementation requires a comprehensive investment across multiple cost categories</strong> - from training and 
                communication to software and organizational restructuring. The phased approach allows companies to spread and control 
                these investments effectively.
              </p>
              <p className="leading-relaxed">
                While certain cost categories are significant (large-scale training, temporary capacity loss, and IT investments), 
                successful cases demonstrate that QRM delivers <strong>dramatic medium-term improvements in lead time, quality, 
                and cost efficiency</strong>.
              </p>
              <p className="leading-relaxed">
                <strong>Key to success:</strong> View these startup costs as a strategic investment in future competitive advantage. 
                When QRM is successfully embedded, organizations benefit long-term through increased agility, satisfied customers, 
                and improved cost structure.
              </p>
            </div>
          </Card>

          {/* Navigation */}
          <div className="text-center mt-12">
            <div className="space-x-4">
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/business-case'}
                className="px-8 py-3"
              >
                ← Calculate ROI Impact
              </Button>
              <Button 
                onClick={() => window.location.href = '/'}
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700"
              >
                Back to Home
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ImplementationCosts;
